'use client';

import * as React from 'react';
import { cx } from '../utils';
import { JIcon } from '../../icons';
import { devWarn } from '../internal/devWarn';
import type { IconIntent } from '../../icons/registry';
import type { StyledProps } from '../types';
import type { UnsafeStyleProps } from '../escape-hatches';

export interface RatingProps extends StyledProps, UnsafeStyleProps {
  /** Integer 1..max, or null when unset. */
  value?: number | null;
  onChange?: (value: number | null) => void;
  /** Maximum value (number of stars). */
  max?: number;
  /** Allow re-clicking the current value (or pressing 0) to clear back to null. */
  clearable?: boolean;
  /** Display-only — no interactive role, no focus ring. */
  readOnly?: boolean;
  size?: 'sm' | 'md';
  disabled?: boolean;
  /** Hide the visible "<n> of <max>" label (still announced via aria-label). */
  hideLabel?: boolean;
  /** Adopter aria-label for the radiogroup (interactive mode). Required. */
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
  testId?: string;
  /** Override the star icon with another registry intent (escape hatch). */
  unsafe_iconIntent?: IconIntent;
}

export function Rating({
  value = null,
  onChange,
  max = 5,
  clearable = false,
  readOnly = false,
  size = 'md',
  disabled = false,
  hideLabel = false,
  className,
  unsafe_style,
  testId,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
  unsafe_iconIntent,
}: RatingProps) {
  const intent: IconIntent = unsafe_iconIntent ?? 'star';
  const itemRefs = React.useRef<Map<number, HTMLButtonElement>>(new Map());
  const [hover, setHover] = React.useState<number | null>(null);

  /* Codex iter-4 #medium: `max` flows directly into `Array.from({ length })`.
     Non-finite (NaN / Infinity) throws RangeError; very large
     (e.g. 1e9 from a config or API) allocates ~GB and OOMs the
     renderer. Clamp to a positive integer within a documented cap. */
  const MAX_CAP = 50;
  const safeMax = (() => {
    if (!Number.isFinite(max) || max <= 0 || !Number.isInteger(max) || max > MAX_CAP) {
      return Math.min(Math.max(1, Math.trunc(Number.isFinite(max) ? max : 5)), MAX_CAP) || 5;
    }
    return max;
  })();
  const _ratingMaxWarnedRef = React.useRef(false);
  if (safeMax !== max && !_ratingMaxWarnedRef.current) {
    _ratingMaxWarnedRef.current = true;
    devWarn(
      `[JKit.Rating] max=${max} is not a positive integer in [1..${MAX_CAP}]; ` +
        `coerced to ${safeMax} to prevent render hangs and OOM (Array.from has unbounded length).`,
    );
  }

  /* Codex iter-7 #medium: `Math.round(NaN) === NaN`, and every
     subsequent comparison (`safeValue ?? 1 === n`) returns false,
     so a non-finite `value` left the radiogroup keyboard-
     unreachable (no cell tabbable, label = "NaN of 5"). Coerce
     non-finite values to null so the picker degrades to "unset"
     instead of a broken state. */
  const safeValue = (() => {
    if (value == null || !Number.isFinite(value)) return null;
    return Math.max(1, Math.min(safeMax, Math.round(value)));
  })();
  /* Fire devWarn once per bad value (not on every render). */
  React.useEffect(() => {
    if (value != null && safeValue !== value) {
      devWarn(
        `[JKit.Rating] value ${value} not in [1..${safeMax}] or non-finite; ` +
          `coerced to ${safeValue}.`,
      );
    }
  }, [value, safeValue, safeMax]);

  const labelText = `${safeValue ?? 0} of ${safeMax}`;
  const items = Array.from({ length: safeMax }, (_, i) => i + 1);

  const commit = (n: number | null) => {
    if (disabled || readOnly) return;
    if (n === safeValue && clearable) onChange?.(null);
    else onChange?.(n);
  };

  const focusItem = (n: number) => {
    const el = itemRefs.current.get(n);
    if (el) el.focus();
  };

  const onItemKey = (e: React.KeyboardEvent, n: number) => {
    if (readOnly || disabled) return;
    let handled = true;
    let next = n;
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') next = Math.min(safeMax, n + 1);
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') next = Math.max(1, n - 1);
    else if (e.key === 'Home') next = 1;
    else if (e.key === 'End') next = safeMax;
    else if (e.key === '0' && clearable) {
      onChange?.(null);
      e.preventDefault();
      return;
    } else if (/^[1-9]$/.test(e.key)) {
      const num = parseInt(e.key);
      if (num <= safeMax) {
        commit(num);
        focusItem(num);
        e.preventDefault();
        return;
      }
      handled = false;
    } else if (e.key === ' ') {
      commit(n);
    } else handled = false;
    if (handled) {
      e.preventDefault();
      if (next !== n) {
        commit(next);
        focusItem(next);
      }
    }
  };

  if (readOnly) {
    return (
      <div
        className={cx('j-rating', `j-rating-${size}`, 'j-rating-readonly', className)}
        style={unsafe_style}
        data-jq-id={testId}
        role="img"
        aria-label={ariaLabelledBy ? undefined : ariaLabel || `${labelText} stars`}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
      >
        <div className="j-rating-items" aria-hidden="true">
          {items.map((n) => {
            /* Read-only has no hover state; "filled" === "committed". */
            const filled = n <= (safeValue ?? 0);
            return (
              <span
                key={n}
                className="j-rating-item j-rating-item-static"
                data-filled={filled || undefined}
              >
                <JIcon
                  intent={intent}
                  size={size === 'sm' ? 16 : 24}
                  fill={filled ? 'currentColor' : 'none'}
                  aria-hidden
                />
              </span>
            );
          })}
        </div>
        {!hideLabel && <span className="j-rating-value">{labelText}</span>}
      </div>
    );
  }

  return (
    <div
      className={cx('j-rating', `j-rating-${size}`, className)}
      style={unsafe_style}
      data-jq-id={testId}
      role="radiogroup"
      aria-label={ariaLabelledBy ? undefined : ariaLabel || 'Rating'}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
      aria-disabled={disabled || undefined}
    >
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- onMouseLeave clears the hover preview; the interactive elements are the inner buttons */}
      <div className="j-rating-items" onMouseLeave={() => setHover(null)}>
        {items.map((n) => {
          /* Three visual states:
             - committed: n <= safeValue → fill="currentColor", theme color
             - hover-preview: n <= hover && n > safeValue → fill="none", theme color (outline only)
             - unfilled: otherwise → fill="none", fg-2 color
             The hover preview is the "considered but not yet confirmed"
             feedback the spec requires — distinct from confirmed fill. */
          const committed = n <= (safeValue ?? 0);
          const previewing = hover !== null && n <= hover && !committed;
          const filled = committed;
          const checked = n === safeValue;
          const tabbable = (safeValue ?? 1) === n;
          return (
            <button
              key={n}
              type="button"
              role="radio"
              ref={(el) => {
                if (el) itemRefs.current.set(n, el);
                else itemRefs.current.delete(n);
              }}
              className="j-rating-item"
              aria-checked={checked}
              aria-label={`${n} ${n === 1 ? 'star' : 'stars'}`}
              data-filled={filled || undefined}
              data-preview={previewing || undefined}
              data-checked={checked || undefined}
              tabIndex={tabbable ? 0 : -1}
              disabled={disabled}
              onMouseEnter={() => setHover(n)}
              onFocus={() => setHover(n)}
              onBlur={() => setHover(null)}
              onClick={() => commit(n)}
              onKeyDown={(e) => onItemKey(e, n)}
            >
              <JIcon
                intent={intent}
                size={size === 'sm' ? 16 : 24}
                fill={filled ? 'currentColor' : 'none'}
                aria-hidden
              />
            </button>
          );
        })}
      </div>
      {!hideLabel && <span className="j-rating-value">{labelText}</span>}
    </div>
  );
}
